import React from 'react';
import { ICON_SIZES, ICON_TONES, IconSize, IconTone } from './tokens';

export interface IconProps {
    /** Size of the icon (maps to design tokens) */
    size?: IconSize;
    /** Color tone of the icon (maps to design tokens) */
    tone?: IconTone;
    /** Custom color override (use sparingly) */
    color?: string;
    /** Additional CSS class */
    className?: string;
    /** Inline styles */
    style?: React.CSSProperties;
    /** Accessible title for the icon */
    title?: string;
    /** ARIA label for accessibility */
    'aria-label'?: string;
    /** Click handler */
    onClick?: (e: React.MouseEvent) => void;
    /** The actual icon component to render */
    children: React.ReactElement;
}

/**
 * Base Icon wrapper component
 * Provides consistent sizing and coloring for all icons in the app
 */
export const Icon: React.FC<IconProps> = ({
    size = 'm',
    tone = 'white',
    color,
    className,
    style,
    title,
    'aria-label': ariaLabel,
    onClick,
    children,
}) => {
    const iconSize = ICON_SIZES[size];
    const iconColor = color || ICON_TONES[tone];

    // Clone the child element and inject size + color
    const childProps = children.props as any;
    const styledIcon = React.cloneElement(children, {
        style: {
            width: iconSize,
            height: iconSize,
            color: iconColor,
            ...(childProps?.style || {}),
            ...style,
        },
        className: childProps?.className
            ? `${childProps.className} ${className || ''}`
            : className,
        'aria-label': ariaLabel || title,
        title: title,
        onClick: onClick,
    } as any);

    return styledIcon;
};
